import { useEffect, useState } from 'react';
import { Undo2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Snapshot {
  id: string;
  synced_at: string;
  diff: {
    added?: string[];
    updated?: Array<{ id: string; before: Record<string, unknown> }>;
    sold?: string[];
  };
}

interface UndoLastSyncButtonProps {
  /** Called after a successful undo so callers can re-fetch inventory. */
  onUndone?: () => void;
}

/**
 * Shows the most recent un-undone Collectr sync within the last 24h and lets
 * the seller revert it (clears sold_at, restores qty/cost/Collectr prices,
 * deletes added rows that have no activity).
 */
export const UndoLastSyncButton = ({ onUndone }: UndoLastSyncButtonProps) => {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchLatest = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSnapshot(null);
      return;
    }
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('collectr_sync_snapshots')
      .select('id, synced_at, diff')
      .eq('user_id', user.id)
      .is('undone_at', null)
      .gte('synced_at', cutoff)
      .order('synced_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setSnapshot((data as unknown as Snapshot) ?? null);
  };

  useEffect(() => {
    void fetchLatest();
  }, []);

  if (!snapshot) return null;

  const counts = {
    added: snapshot.diff?.added?.length ?? 0,
    updated: snapshot.diff?.updated?.length ?? 0,
    sold: snapshot.diff?.sold?.length ?? 0,
  };
  const totalTouched = counts.added + counts.updated + counts.sold;
  if (totalTouched === 0) return null;

  const handleUndo = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('undo_collectr_sync', {
        _snapshot_id: snapshot.id,
      });
      if (error) throw error;
      const result = (data as Record<string, number>) ?? {};
      const parts: string[] = [];
      if (result.reverted_updates) parts.push(`${result.reverted_updates} updates reverted`);
      if (result.reverted_sold) parts.push(`${result.reverted_sold} restored from sold`);
      if (result.deleted_added) parts.push(`${result.deleted_added} added items removed`);
      if (result.skipped_added) parts.push(`${result.skipped_added} kept (had activity)`);
      toast.success('Last sync undone', {
        description: parts.join(' • ') || 'Done.',
      });
      setSnapshot(null);
      setConfirmOpen(false);
      onUndone?.();
    } catch (err) {
      console.error('[UndoLastSyncButton]', err);
      toast.error('Undo failed', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const ago = formatDistanceToNow(new Date(snapshot.synced_at), { addSuffix: true });

  return (
    <>
      <div className="flex">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          className="bg-white/[0.05] border-white/15 hover:border-active-gold/40 h-9 gap-1.5 w-full"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Undo sync ({ago})
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !busy && setConfirmOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo last sync?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>Reverts the sync from {ago}:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  {counts.sold > 0 && <li>Restore {counts.sold} sold item{counts.sold === 1 ? '' : 's'}</li>}
                  {counts.updated > 0 && <li>Roll back qty, cost, and prices on {counts.updated} item{counts.updated === 1 ? '' : 's'}</li>}
                  {counts.added > 0 && <li>Delete {counts.added} added item{counts.added === 1 ? '' : 's'} (if untouched)</li>}
                </ul>
                <p className="text-xs">Available for 24h.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleUndo();
              }}
              disabled={busy}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Undo sync'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
